import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import api from '../../utils/api'; 
import { format } from 'date-fns';
import { Counsellor } from '../../types/counsellor';

export default function ArchivedCounsellors() {
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCounsellor, setSelectedCounsellor] = useState<Counsellor | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    fetchArchivedCounsellors();
  }, []);

  const fetchArchivedCounsellors = async () => {
    try {
      const response = await api.get('/api/counsellors/archived');
      if (Array.isArray(response.data)) {
        console.log('Archived counsellors:', response.data); // Debug log
        setCounsellors(response.data);
      } else {
        console.error('Expected array of counsellors but got:', response.data);
        setCounsellors([]);
      }
    } catch (error) {
      console.error('Error fetching archived counsellors:', error);
      setCounsellors([]);
    }
  };

  const handleReactivate = async (counsellor: Counsellor) => {
    try {
      await api.post(`/api/counsellors/${counsellor._id}/reactivate`);
      await fetchArchivedCounsellors();
    } catch (error) {
      console.error('Error reactivating counsellor:', error);
    }
  };

  const filteredCounsellors = counsellors.filter(counsellor =>
    `${counsellor.firstName ?? ''} ${counsellor.lastName ?? ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (counsellor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  // Get paginated data
  const paginatedCounsellors = filteredCounsellors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search counsellors"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page when search changes
          }}
          sx={{ 
            minWidth: 200,
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)'
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'rgba(255, 255, 255, 0.9)'
            },
            '& .MuiOutlinedInput-input': {
              color: 'white'
            }
          }}
        />
      </Box>

      {/* Table */}
      <div className="overflow-x-auto glass-card rounded-lg">
        <table className="min-w-full">
          <thead className="glass-card-light">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Archive Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {paginatedCounsellors.map((counsellor) => (
              <tr key={counsellor._id} className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium theme-text-primary">
                    {counsellor.firstName} {counsellor.lastName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm theme-text-secondary">
                    {counsellor.email || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    <Chip
                      label={counsellor.isArchived ? 'Archived' : 'Active'}
                      color={counsellor.isArchived ? 'error' : 'success'}
                      size="small"
                    />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm theme-text-secondary">
                    {counsellor.archiveDate
                      ? format(new Date(counsellor.archiveDate), 'PPp')
                      : 'N/A'
                    }
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => {
                      setSelectedCounsellor(counsellor);
                      setOpenDialog(true);
                    }}
                    className="text-blue-400 font-bold hover:text-blue-300 transition-colors mr-4"
                  >
                    View Details
                  </button>
                  {counsellor.isArchived && (
                    <button
                      onClick={() => handleReactivate(counsellor)}
                      className="text-blue-400 font-bold hover:text-blue-300 transition-colors"
                    >
                      Reactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
              page === 0
                ? 'bg-white/5 text-white/40'
                : 'bg-white/10 theme-text-primary hover:bg-white/20'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= Math.ceil(filteredCounsellors.length / rowsPerPage) - 1}
            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
              page >= Math.ceil(filteredCounsellors.length / rowsPerPage) - 1
                ? 'bg-white/5 text-white/40'
                : 'bg-white/10 theme-text-primary hover:bg-white/20'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm theme-text-secondary">
              {filteredCounsellors.length === 0 
                ? 'No results' 
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, filteredCounsellors.length)} of ${filteredCounsellors.length} results`
              }
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handleChangePage(page - 1)}
                disabled={page === 0}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/20 bg-white/10 text-sm font-medium ${
                  page === 0
                    ? 'text-white/40'
                    : 'theme-text-primary hover:bg-white/20'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => handleChangePage(page + 1)}
                disabled={page >= Math.ceil(filteredCounsellors.length / rowsPerPage) - 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/20 bg-white/10 text-sm font-medium ${
                  page >= Math.ceil(filteredCounsellors.length / rowsPerPage) - 1
                    ? 'text-white/40'
                    : 'theme-text-primary hover:bg-white/20'
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 'bold', 
          color: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          Archived Counsellor Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedCounsellor && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Name:</strong>
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', ml: 2 }}>
                  {selectedCounsellor.firstName} {selectedCounsellor.lastName}
                </Typography>
              </Box>

              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Email:</strong>
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', ml: 2 }}>
                  {selectedCounsellor.email || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Status:</strong>
                </Typography>
                <Box sx={{ ml: 2, mt: 1 }}>
                  <Chip
                    label={selectedCounsellor.isArchived ? 'Archived' : 'Active'}
                    color={selectedCounsellor.isArchived ? 'error' : 'success'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Archive Date:</strong>
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', ml: 2 }}>
                  {selectedCounsellor.archiveDate
                    ? format(new Date(selectedCounsellor.archiveDate), 'PPp')
                    : 'N/A'
                  }
                </Typography>
              </Box>

              {selectedCounsellor.archiveReason && (
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(255, 193, 7, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 193, 7, 0.3)'
                }}>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 193, 7, 0.9)', mb: 1, fontWeight: 600 }}>
                    <strong>Archive Reason:</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', ml: 2, whiteSpace: 'pre-wrap' }}>
                    {selectedCounsellor.archiveReason}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            variant="contained"
            sx={{
              backgroundColor: 'rgba(100, 181, 246, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(100, 181, 246, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(100, 181, 246, 0.3)',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
